// @flow

import { POSTHOG_EVENTS, posthogCapture } from '../posthog'
import {
  type Wizard$RecommendedProductClickedEventPayload,
  type Wizard$CompletedEventPayload,
  type Wizard$StartedEventPayload,
  type Wizard$AddedToCartEventPayload,
  type Wizard$RoutineAddedToCartEventPayload,
  type Wizard$AttributeMatchingListExpandedEventPayload,
  type Wizard$RecommendedProductInfoClickedEventPayload,
  type Wizard$MoreProductsLoadedEventPayload,
  type Wizard$ProductsSortedEventPayload,
  WIZARD_EVENTS
} from '../types'

export function posthogCaptureStartedEvent (eventPayload: Wizard$StartedEventPayload): void {
  posthogCapture(POSTHOG_EVENTS.STARTED, {
    interface: 'wizard',
    subscriberId: eventPayload.subscriberId,
    wizardId: eventPayload.wizardId,
    landingPageId: eventPayload.landingPageId,
    eventDescription: 'landing page button clicked'
  })
}

export function posthogCaptureCompletedEvent (eventPayload: Wizard$CompletedEventPayload): void {
  posthogCapture(POSTHOG_EVENTS.COMPLETED, {
    interface: 'wizard',
    subscriberId: eventPayload.subscriberId,
    wizardId: eventPayload.wizardId,
    productIds: eventPayload.productIds,
    type: eventPayload.type,
    utility_max: eventPayload.utilityMax,
    utility_average: eventPayload.utilityAverage,
    eventDescription: 'recommended products page displayed'
  })
}

export function posthogCaptureRecommendedProductClickedEvent (eventPayload: Wizard$RecommendedProductClickedEventPayload): void {
  posthogCapture(POSTHOG_EVENTS.RECOMMENDED_PRODUCT_CLICKED, {
    interface: 'wizard',
    subscriberId: eventPayload.subscriberId,
    wizardId: eventPayload.wizardId,
    productId: eventPayload.productId,
    utility: eventPayload.utility,
    eventDescription: 'recommended product clicked'
  })
}

export function posthogCaptureAddedToCartEvent (eventPayload: Wizard$AddedToCartEventPayload): void {
  posthogCapture(POSTHOG_EVENTS.ATC_CLICKED, {
    interface: 'wizard',
    subscriberId: eventPayload.subscriberId,
    wizardId: eventPayload.wizardId,
    productId: eventPayload.productId,
    eventDescription: 'add to cart button clicked'
  })
}

export function posthogCaptureRoutineAddedToCartEvent (eventPayload: Wizard$RoutineAddedToCartEventPayload): void {
  posthogCapture(POSTHOG_EVENTS.ATC_ROUTINE_BUTTON_CLICKED, {
    interface: 'wizard',
    subscriberId: eventPayload.subscriberId,
    wizardId: eventPayload.wizardId,
    productIds: eventPayload.productIds,
    eventDescription: 'add to cart routine button clicked'
  })
}

export function posthogCaptureAttributeMatchingListExpandedEvent (eventPayload: Wizard$AttributeMatchingListExpandedEventPayload): void {
  posthogCapture(POSTHOG_EVENTS.MATCHING_PANEL_EXPANDED, {
    interface: 'wizard',
    subscriberId: eventPayload.subscriberId,
    wizardId: eventPayload.wizardId,
    productId: eventPayload.productId,
    eventDescription: 'recommended product attribute matching list expanded'
  })
}

export function posthogCaptureRecommendedProductInfoClickedEvent (eventPayload: Wizard$RecommendedProductInfoClickedEventPayload): void {
  posthogCapture(POSTHOG_EVENTS.PRODUCT_INFO_BUTTON_CLICKED, {
    interface: 'wizard',
    subscriberId: eventPayload.subscriberId,
    wizardId: eventPayload.wizardId,
    productId: eventPayload.productId,
    eventDescription: 'recommended product info button clicked'
  })
}

export function posthogCaptureMoreProductsLoadedEvent (eventPayload: Wizard$MoreProductsLoadedEventPayload): void {
  posthogCapture(POSTHOG_EVENTS.MORE_PRODUCTS_LOADED, {
    interface: 'wizard',
    subscriberId: eventPayload.subscriberId,
    wizardId: eventPayload.wizardId,
    productIds: eventPayload.productIds,
    eventDescription: 'clicked the button to load more products'
  })
}

export function posthogCaptureProductsSortedEvent (eventPayload: Wizard$ProductsSortedEventPayload): void {
  posthogCapture(POSTHOG_EVENTS.PRODUCTS_SORTED, {
    interface: 'wizard',
    subscriberId: eventPayload.subscriberId,
    wizardId: eventPayload.wizardId,
    sorting: eventPayload.sorting,
    eventDescription: 'selected an option to sort the products'
  })
}

const eventsWrapper = {
  started: ({ detail }) => posthogCaptureStartedEvent(detail),
  completed: ({ detail }) => posthogCaptureCompletedEvent(detail),
  recommendedProductClicked: ({ detail }) => posthogCaptureRecommendedProductClickedEvent(detail),
  addedToCart: ({ detail }) => posthogCaptureAddedToCartEvent(detail),
  routineAddedToCart: ({ detail }) => posthogCaptureRoutineAddedToCartEvent(detail),
  attributeMatchingListExpanded: ({ detail }) => posthogCaptureAttributeMatchingListExpandedEvent(detail),
  recommendedProductInfoClicked: ({ detail }) => posthogCaptureRecommendedProductInfoClickedEvent(detail),
  moreProductsLoaded: ({ detail }) => posthogCaptureMoreProductsLoadedEvent(detail),
  productsSorted: ({ detail }) => posthogCaptureProductsSortedEvent(detail)
}

export function registerPosthogListeners (): void {
  window.addEventListener(WIZARD_EVENTS.Started, eventsWrapper.started)
  window.addEventListener(WIZARD_EVENTS.Completed, eventsWrapper.completed)
  window.addEventListener(WIZARD_EVENTS.RecommendedProductClicked, eventsWrapper.recommendedProductClicked)
  window.addEventListener(WIZARD_EVENTS.AddedToCartEvent, eventsWrapper.addedToCart)
  window.addEventListener(WIZARD_EVENTS.RoutineAddedToCartEvent, eventsWrapper.routineAddedToCart)
  window.addEventListener(WIZARD_EVENTS.AttributeMatchingListExpandedEvent, eventsWrapper.attributeMatchingListExpanded)
  window.addEventListener(WIZARD_EVENTS.RecommendedProductInfoClickedEvent, eventsWrapper.recommendedProductInfoClicked)
  window.addEventListener(WIZARD_EVENTS.MoreProductsLoadedEvent, eventsWrapper.moreProductsLoaded)
  window.addEventListener(WIZARD_EVENTS.ProductsSortedEvent, eventsWrapper.productsSorted)
}

export function unregisterPosthogListeners (): void {
  window.removeEventListener(WIZARD_EVENTS.Started, eventsWrapper.started)
  window.removeEventListener(WIZARD_EVENTS.Completed, eventsWrapper.completed)
  window.removeEventListener(WIZARD_EVENTS.RecommendedProductClicked, eventsWrapper.recommendedProductClicked)
  window.removeEventListener(WIZARD_EVENTS.AddedToCartEvent, eventsWrapper.addedToCart)
  window.removeEventListener(WIZARD_EVENTS.RoutineAddedToCartEvent, eventsWrapper.routineAddedToCart)
  window.removeEventListener(WIZARD_EVENTS.AttributeMatchingListExpandedEvent, eventsWrapper.attributeMatchingListExpanded)
  window.removeEventListener(WIZARD_EVENTS.RecommendedProductInfoClickedEvent, eventsWrapper.recommendedProductInfoClicked)
  window.removeEventListener(WIZARD_EVENTS.MoreProductsLoadedEvent, eventsWrapper.moreProductsLoaded)
  window.removeEventListener(WIZARD_EVENTS.ProductsSortedEvent, eventsWrapper.productsSorted)
}
